import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useCashflowOverview, usePortfolioSummary, useProperties } from "../hooks/realestate";

function formatEur(cents: number | null): string {
  if (cents === null) return "—";
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function RealEstateList() {
  const { t } = useTranslation();
  const list = useProperties({ active: true });
  const summary = usePortfolioSummary();

  if (list.isLoading) {
    return (
      <div className="fv-card p-10 text-center" style={{ color: "var(--muted)" }}>
        …
      </div>
    );
  }
  const properties = list.data?.properties ?? [];
  const isEmpty = properties.length === 0;

  return (
    <section className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_assets")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("realestate.list.title")}</h1>
          {summary.data && summary.data.propertyCount > 0 && (
            <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
              {summary.data.propertyCount}{" "}
              {summary.data.propertyCount === 1
                ? t("realestate.property_singular", { defaultValue: "Immobilie" })
                : t("realestate.property_plural", { defaultValue: "Immobilien" })}
            </div>
          )}
        </div>
        <Link
          to="/realestate/new"
          className="px-3 py-2 rounded-lg text-white text-sm font-medium"
          style={{ background: "var(--accent)" }}
        >
          + {t("realestate.list.new")}
        </Link>
      </div>

      {summary.data && summary.data.propertyCount > 0 && (
        <div
          className="fv-card grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x"
          style={{ borderColor: "var(--border)" }}
        >
          <StatMini
            label={t("realestate.list.summary.market_value")}
            value={formatEur(summary.data.totalMarketValueCents)}
            emphasis
          />
          <StatMini
            label={t("realestate.list.summary.remaining_debt")}
            value={formatEur(summary.data.totalRemainingDebtCents)}
          />
          <StatMini
            label={t("realestate.list.summary.equity")}
            value={formatEur(summary.data.totalEquityCents)}
            tone="pos"
          />
          <StatMini
            label={t("realestate.list.summary.lifetime_cashflow")}
            value={formatEur(summary.data.totalLifetimeBankNetCents)}
            sub={`YTD ${formatEur(summary.data.totalYtdBankNetCents)}`}
            tone={summary.data.totalLifetimeBankNetCents >= 0 ? "pos" : "neg"}
          />
        </div>
      )}

      {isEmpty ? (
        <div className="fv-card p-10 text-center">
          <p style={{ color: "var(--text)" }} className="mb-3">
            {t("realestate.list.empty")}
          </p>
          <Link
            to="/realestate/new"
            className="inline-block px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            {t("realestate.list.empty_cta")}
          </Link>
        </div>
      ) : (
        <div className="fv-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs"
                style={{ background: "var(--border-soft)", color: "var(--muted)" }}
              >
                <th className="px-3 py-2 font-semibold">{t("realestate.list.column.name")}</th>
                <th className="px-3 py-2 font-semibold">
                  {t("realestate.list.column.address")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("realestate.list.column.purchase_price")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("realestate.list.column.cashflow")}
                </th>
              </tr>
            </thead>
            <tbody>
              {properties.map((p) => (
                <tr
                  key={p.id}
                  className="border-b transition-colors hover:bg-[var(--row-hover)]"
                  style={{ borderColor: "var(--border-soft)" }}
                >
                  <td className="px-3 py-2.5">
                    <Link to={`/realestate/${p.id}`} style={{ color: "var(--accent)" }}>
                      {p.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2.5" style={{ color: "var(--muted)" }}>
                    {p.street}, {p.postalCode} {p.city}
                  </td>
                  <td className="px-3 py-2.5 text-right num">
                    {formatEur(p.purchasePriceCents)}
                  </td>
                  <td className="px-3 py-2.5">
                    <CashflowCell propertyId={p.id} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function StatMini({
  label,
  value,
  sub,
  tone,
  emphasis,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "pos" | "neg";
  emphasis?: boolean;
}) {
  const color = tone === "pos" ? "var(--pos)" : tone === "neg" ? "var(--neg)" : "var(--text)";
  return (
    <div className="p-4">
      <div className="fv-kpi-label mb-1">{label}</div>
      <div
        className={`${emphasis ? "text-2xl" : "text-xl"} font-bold tracking-tight num`}
        style={{ color }}
      >
        {value}
      </div>
      {sub && (
        <div className="text-xs mt-1 num" style={{ color: "var(--muted)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function CashflowCell({ propertyId }: { propertyId: string }) {
  const overview = useCashflowOverview(propertyId);
  if (!overview.data)
    return (
      <span style={{ color: "var(--subtle)" }}>
        …
      </span>
    );
  const v = overview.data.lifetimeBankNetCents;
  return (
    <div className="text-right">
      <div className="num" style={{ color: v >= 0 ? "var(--pos)" : "var(--neg)" }}>
        {formatEur(v)}
      </div>
      <div className="text-xs num" style={{ color: "var(--muted)" }}>
        YTD {formatEur(overview.data.ytdBankNetCents)}
      </div>
    </div>
  );
}
